import { useEffect, useCallback, RefObject } from 'react';

/**
 * Options for focus trap behavior
 */
export interface FocusTrapOptions {
  /**
   * Enable the focus trap
   */
  enabled?: boolean;
  /**
   * Element to focus when trap activates
   */
  initialFocus?: RefObject<HTMLElement>;
  /**
   * Element to focus when trap deactivates
   */
  returnFocus?: RefObject<HTMLElement>;
}

/**
 * Hook to trap focus within a container element.
 * Essential for modal dialogs, drawers, and other overlay components.
 * Implements WCAG 2.1.2 (No Keyboard Trap) by allowing proper focus cycling.
 *
 * @param containerRef - Reference to the container element that traps focus
 * @param options - Configuration options for the focus trap
 *
 * @example
 * const modalRef = useRef<HTMLDivElement>(null);
 * const closeButtonRef = useRef<HTMLButtonElement>(null);
 *
 * useFocusTrap(modalRef, {
 *   enabled: isOpen,
 *   initialFocus: closeButtonRef,
 * });
 */
export const useFocusTrap = (
  containerRef: RefObject<HTMLElement>,
  options: FocusTrapOptions = {}
) => {
  const { enabled = true, initialFocus, returnFocus } = options;

  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];

    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    return Array.from(containerRef.current.querySelectorAll<HTMLElement>(selector));
  }, [containerRef]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      // Safety check - should not happen since we check length > 0 above
      if (!firstElement || !lastElement) return;

      // Shift + Tab (backwards)
      if (event.shiftKey) {
        if (activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      }
      // Tab (forwards)
      else {
        if (activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },
    [getFocusableElements]
  );

  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    // Store previously focused element
    const previouslyFocused = document.activeElement as HTMLElement;

    // Capture returnFocus ref value at effect start to avoid stale reference in cleanup
    const returnFocusElement = returnFocus?.current;

    // Set initial focus
    const focusableElements = getFocusableElements();
    const elementToFocus = initialFocus?.current || focusableElements[0];
    elementToFocus?.focus();

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Return focus to previously focused element
      const elementToReturn = returnFocusElement || previouslyFocused;
      elementToReturn?.focus();
    };
  }, [enabled, containerRef, getFocusableElements, handleKeyDown, initialFocus, returnFocus]);
};
